import React from "react";
import { motion } from "framer-motion";
import { useI18n } from "@/i18n";
import { Camera, User, Sparkles, Megaphone, Compass, Share2, Globe, PenTool, ArrowUpRight } from "lucide-react";

const ICONS = [Camera, User, Sparkles, Megaphone, Compass, Share2, Globe, PenTool];

export default function ServicesSection() {
  const { t } = useI18n();

  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative bg-black py-28 md:py-40 overflow-hidden"
    >
      <div className="absolute inset-0 bg-gradient-to-b from-zinc-950 via-black to-black" />
      <div className="relative container mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-12 lg:gap-20 mb-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8 }}
            className="flex items-start gap-3"
          >
            <span className="w-10 h-px bg-lux mt-3" />
            <span className="text-[11px] uppercase tracking-[0.3em] text-lux">
              {t.services.label}
            </span>
          </motion.div>
          <div>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.9 }}
              className="font-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter text-balance"
            >
              {t.services.title}
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.9, delay: 0.15 }}
              className="mt-6 text-base md:text-lg text-white/60 max-w-2xl"
            >
              {t.services.subtitle}
            </motion.p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5">
          {t.services.items.map((s, i) => {
            const Icon = ICONS[i % ICONS.length];
            return (
              <motion.article
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.7, delay: (i % 3) * 0.1 }}
                data-testid={`service-card-${i}`}
                className="group relative bg-black p-8 md:p-10 min-h-[340px] flex flex-col justify-between overflow-hidden cursor-default"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-white/[0.04] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                <div className="absolute -inset-px border border-transparent group-hover:border-white/15 transition-colors duration-700" />

                <div className="relative">
                  <div className="flex items-start justify-between mb-10">
                    <div className="w-12 h-12 rounded-full luxury-glass flex items-center justify-center transition-transform duration-500 group-hover:scale-110">
                      <Icon size={18} strokeWidth={1.4} className="text-lux" />
                    </div>
                    <span className="text-xs text-lux/60 font-display tracking-widest">
                      0{i + 1}
                    </span>
                  </div>
                  <h3 className="font-display text-2xl md:text-3xl font-light tracking-tight mb-3">
                    {s.title}
                  </h3>
                  <p className="text-sm md:text-[15px] text-white/60 leading-relaxed">
                    {s.desc}
                  </p>
                </div>

                <div className="relative mt-8 flex flex-wrap items-center justify-between gap-3 pt-6 border-t border-white/10">
                  <ul className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] uppercase tracking-[0.2em] text-white/40">
                    {s.benefits.map((b) => (
                      <li key={b}>· {b}</li>
                    ))}
                  </ul>
                  <ArrowUpRight
                    size={18}
                    className="text-white/40 group-hover:text-white group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all duration-500"
                  />
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
